import React, { useState, useEffect, useCallback } from 'react';
import { formatNumber } from '../../utils/formatters';
import useOrderBookStore from '../../store/useOrderBookStore';
import API_CONFIG from '../../config/api';

const OrderBook = ({ pair, onOrderSelect }) => {
  const {
    selectedPair,
    setSelectedPair,
    precision,
    setPrecision,
    fetchOrderBook,
    getProcessedSellOrders,
    getProcessedBuyOrders,
    getMaxDepth,
    getSpread,
    getCurrentPrice,
    isLoading,
    error,
    startAutoRefresh
  } = useOrderBookStore();
  const [marketData, setMarketData] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Get quote currency from market data
  const getQuoteCurrency = (pairCode) => {
    if (!marketData || !marketData.pairs_data || !marketData.pairs_data[pairCode]) return 'USDT';
    return marketData.pairs_data[pairCode].pair_data?.quote?.code || 'USDT';
  };

  useEffect(() => {
    if (pair && pair !== selectedPair) {
      setSelectedPair(pair);
    }
  }, [pair, selectedPair, setSelectedPair]);


  useEffect(() => {
    if (selectedPair) {
      fetchOrderBook(selectedPair);
      const cleanup = startAutoRefresh(50000); // Update every 2 seconds
      return cleanup;
    }
  }, [selectedPair, fetchOrderBook, startAutoRefresh]);

  const handleOrderClick = useCallback((order, type) => {
    if (onOrderSelect) {
      onOrderSelect({
        price: order.price,
        quantity: order.quantity,
        type: type === 'sell' ? 'buy' : 'sell', // Opposite operation
      });
    }
  }, [onOrderSelect]);

  const processedSellOrders = getProcessedSellOrders();
  const processedBuyOrders = getProcessedBuyOrders();
  const maxDepth = getMaxDepth();
  const spread = getSpread();
  const currentPrice = getCurrentPrice();

  useEffect(() => {
    const fetchMarketData = async () => {
      try {
        setLoading(true);
        const response = await fetch(`${API_CONFIG.BASE_URL}/v1/homepage-external-price/`);
        const data = await response.json();
        setMarketData(data);
      } catch (error) {
        console.error('Error fetching market data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchMarketData();
    
    // Set up polling to refresh data every 30 seconds
    const interval = setInterval(fetchMarketData, 30000);
    return () => clearInterval(interval);
  }, []);


  const precisionOptions = ['0.1', '1', '10', '100', '1000'];

  if (isLoading && processedSellOrders.length === 0 && processedBuyOrders.length === 0) {
    return (
      <div className="h-full bg-gray-900 flex items-center justify-center p-4">
        <div className="text-white text-sm sm:text-base">Loading order book...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="h-full bg-gray-900 flex items-center justify-center p-4">
        <div className="text-red-400 text-center">
          <div className="text-sm sm:text-base">Error loading order book</div>
          <div className="text-xs text-white mt-1">{error}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full bg-gray-900 flex flex-col border border-gray-700 rounded-lg">
      {/* Header */}
      <div className="p-2 sm:p-3 border-b border-gray-700 bg-gray-800 rounded-t-lg">
        <div className="flex justify-between items-center">
          <div>
            <h3 className="text-sm sm:text-base font-semibold text-white">Order Book</h3>
          </div>
          <div className="flex items-center space-x-2">
            <select
              value={precision.toString()}
              onChange={(e) => setPrecision(parseFloat(e.target.value))}
              className="text-xs bg-gray-700 border border-gray-600 rounded px-2 py-1 text-white focus:outline-none focus:ring-1 focus:ring-blue-500"
            >
              {precisionOptions.map(option => (
                <option key={option} value={option}>{option}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <div className="flex-1 flex flex-col min-h-0">
        {/* Headers */}
        <div className="grid grid-cols-3 gap-1 sm:gap-2 px-2 sm:px-3 py-1 sm:py-2 bg-gray-800 text-xs font-medium text-white border-b border-gray-700">
          <div className="text-left">Price</div>
          <div className="text-right">Size</div>
          <div className="text-right">Total</div>
        </div>

        {/* Sell Orders (Asks) */}
        <div className="flex-1 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-600 scrollbar-track-gray-800">
          {processedSellOrders.length > 0 ? (
            processedSellOrders.map((order, index) => (
              <OrderRow
                key={`sell-${index}`}
                order={order}
                type="sell"
                maxDepth={maxDepth}
                onClick={() => handleOrderClick(order, 'sell')}
              />
            ))
          ) : (
            <div className="px-2 sm:px-3 py-3 sm:py-4 text-center text-white text-xs">
              No sell orders
            </div>
          )}
        </div>

        {/* Current Price and Spread */}
        <div className="px-2 sm:px-3 py-1 sm:py-2 bg-gray-800 border-y border-gray-700">
          <div className="text-center">
            {(() => {
              // Get price from market data API response
              const marketPrice = marketData?.pairs_data?.[selectedPair]?.price;
              const displayPrice = marketPrice || currentPrice;
              
              return displayPrice > 0 ? (
                <div className="text-xs sm:text-sm font-medium text-white">
                  {formatNumber(displayPrice, 2)} {getQuoteCurrency(selectedPair)}
                </div>
              ) : (
                <div className="text-xs text-white">0</div>
              );
            })()}
            {spread > 0 && (
              <div className="text-xs mt-1">
                <span className="text-white">Spread: </span>
                <span className="text-white">
                  {formatNumber(spread, 2)}
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Buy Orders (Bids) */}
        <div className="flex-1 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-600 scrollbar-track-gray-800">
          {processedBuyOrders.length > 0 ? (
            processedBuyOrders.map((order, index) => (
              <OrderRow
                key={`buy-${index}`}
                order={order}
                type="buy"
                maxDepth={maxDepth}
                onClick={() => handleOrderClick(order, 'buy')}
              />
            ))
          ) : (
            <div className="px-2 sm:px-3 py-3 sm:py-4 text-center text-white text-xs">
              No buy orders
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const OrderRow = ({ order, type, maxDepth, onClick }) => {
  const depthPercentage = maxDepth > 0 ? (order.depth / maxDepth) * 100 : 0;
  const isSell = type === 'sell';

  return (
    <div
      className={`relative grid grid-cols-3 gap-1 sm:gap-2 px-2 sm:px-3 py-1 sm:py-1.5 text-xs cursor-pointer transition-colors duration-150 hover:bg-gray-700/50`}
      onClick={onClick}
    >
      {/* Depth visualization */}
      <div
        className={`absolute inset-0 ${isSell
            ? 'bg-red-900/20'
            : 'bg-green-900/20'
          }`}
        style={{ width: `${depthPercentage}%` }}
      />

      {/* Content */}
      <div className={`relative font-mono text-xs sm:text-sm ${isSell ? 'text-red-400' : 'text-white'}`}>
        {formatNumber(order.price, 2)}
      </div>
      <div className="relative text-right font-mono text-xs sm:text-sm text-white">
        {formatNumber(order.quantity, 6)}
      </div>
      <div className="relative text-right font-mono text-xs sm:text-sm text-white">
        {formatNumber(order.total, 2)}
      </div>
    </div>
  );
};

export default OrderBook;